import HomePage from 'components/templates/navigationPageTemplates/HomePage'
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ASSESSMENT_SELF, ONBOARDING, PERMISSION_LEVEL } from 'routes/constants'
import { updateStates } from 'services/userServices'
import {
  getGoals,
  getGoalInfoById,
  getGoalsProgress,
  getGoalSteps
} from 'utils/getSupportingInfo'
import { compileGoalSteps } from 'services/goalServices'
import { getUpdateLocalUser } from 'utils/localStorage'
import { isAllowedFeature, permitted } from 'utils/permissionCheck'
import {
  COHORT_GOAL_FACILITATION_RANKINGS_URL,
  FEATURE_KEYS
} from 'services/constants'
import FullAssessmentHome from 'components/organisms/assessments/fullAssessment/FullAssessmentHome'
import RaterHomeLayout from 'components/organisms/home/RaterHomeLayout'
import HomePopups from 'components/molecules/home/HomePopups'
import OverlayLoading from 'components/atoms/OverlayLoading'
import WelcomeBanner from 'components/molecules/home/WelcomeBanner'
import { Stack, Tab, Typography } from '@mui/material'
import moment from 'moment'
import RowStack from 'components/atoms/container/RowStack'
import { useTranslation } from 'react-i18next'
import ToggleTabs from 'components/atoms/button/ToggleTabs'
import CurrentPathArea from 'components/organisms/home/CurrentPathArea'
import ActivityLists from 'components/organisms/home/ActivityLists'
import PathList from 'components/organisms/home/PathList'
import MyPathArea from 'components/organisms/home/MyPathArea'
import { getRequest } from 'utils/api'
import NoGoalContent from 'components/molecules/home/NoGoalContent'

const MyJourney = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [user, setUser] = useState(undefined)
  const [goals, setGoals] = useState([])
  const [currentGoal, setCurrentGoal] = useState(null)
  const [hasLicense, setHasLicense] = useState(false)
  const [fullAssessmentUser, setFullAssessmentUser] = useState(false)
  const [loading, setLoading] = useState(false)
  const [startingGoalName, setStartingGoalName] = useState(null)
  const [futureSession, setFutureSession] = useState(null)
  const [cohortRanking, setCohortRanking] = useState(null)
  const [activeTab, setActiveTab] = useState(
    location?.state?.showMyPath ? 1 : 0
  )

  const fetchGoalsData = async () => {
    const goalsRes = await getGoals(true)
    const resProgress = await getGoalsProgress()
    // map goal progress to goals
    const updatedGoals = await Promise.all(
      goalsRes.map(async goal => {
        const progress = resProgress.find(p => p.goalId === goal.id)
        goal.progress = progress
        goal.active = !!(
          !progress?.completed &&
          (goal.currentGoal || progress?.started)
        )

        if (goal.active || goal.opensAt) {
          const steps = await getGoalSteps(goal.id)
          const { stats } = compileGoalSteps(steps, progress?.currentGoalStepId)
          goal.stats = stats
        }
        return goal
      })
    )
    const sortedGoals = updatedGoals
      .filter(g => g.display)
      .sort((a, b) => {
        if (a.currentGoal && !b.currentGoal) return -1
        if (!a.currentGoal && b.currentGoal) return 1
        if (a.active && !b.active) return -1
        if (!a.active && b.active) return 1
        if (!a.isLocked && b.isLocked) return -1
        if (a.isLocked && !b.isLocked) return 1
        return 0
      })
    setGoals(sortedGoals)
    const userCurrentGoal = sortedGoals.find(
      g => g.currentGoal || g.completedCurrentGoal
    )
    setCurrentGoal(userCurrentGoal)
    return userCurrentGoal
  }

  useEffect(() => {
    const fetchData = async () => {
      const updatedUser = await getUpdateLocalUser()
      const fullAssessmentPermitted = isAllowedFeature(
        FEATURE_KEYS.FULL_LEADERSHIP_ASSESSMENT
      )
      setFullAssessmentUser(fullAssessmentPermitted)
      if (!fullAssessmentPermitted) {
        const userCurrentGoal = await fetchGoalsData()
        if (
          updatedUser.hasLicense &&
          !updatedUser.onboardingSelfAssessmentComplete &&
          isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT)
        ) {
          if (
            // check if user has completed onboarding
            !Boolean(updatedUser.department && updatedUser.job) ||
            !updatedUser.userStates?.currentGoalId
          ) {
            navigate(ONBOARDING)
          } else {
            // if user has completed onboarding but not completed self assessment
            navigate(ASSESSMENT_SELF, {
              state: {
                goalId: updatedUser.userStates.currentGoalId,
                firstTime: true
              }
            })
          }
        } else if (updatedUser.hasLicense) {
          if (updatedUser.userStates.startingGoalId) {
            const goalInfo = await getGoalInfoById(
              updatedUser.userStates.startingGoalId
            )
            setStartingGoalName(goalInfo.name)
            updateStates({ startingGoalId: '' })
          }
          if (updatedUser.userStates.currentGoalId) {
            // Check if there is a future session
            const futureCohort = updatedUser.cohorts.find(c => {
              const foundSession = c.cohortSessions?.find(
                s =>
                  s.goalId === updatedUser.userStates.currentGoalId &&
                  moment.utc(s.startDate).isAfter(moment.utc())
              )
              if (foundSession) {
                setFutureSession(foundSession)
                return true
              }
              return false
            })
            // Check if user completed self assessment
            if (userCurrentGoal && !userCurrentGoal.hasAssessment) {
              let cohortName = futureCohort?.name
              if (!cohortName) {
                const currentCohort = updatedUser.cohorts.find(c => {
                  const foundSession = c.cohortSessions?.find(
                    s =>
                      s.goalId === updatedUser.userStates.currentGoalId &&
                      moment.utc(s.endDate).isAfter(moment.utc())
                  )
                  if (foundSession) {
                    return true
                  }
                  return false
                })
                cohortName = currentCohort?.name
              }
              navigate(ASSESSMENT_SELF, {
                state: {
                  goalId: updatedUser.userStates.currentGoalId,
                  currentGoal: true,
                  cohortUser: Boolean(cohortName)
                }
              })
            }
            if (!futureCohort) {
              const rankings = await getRequest(
                COHORT_GOAL_FACILITATION_RANKINGS_URL
              )
              if (rankings.status === 200) {
                setCohortRanking(rankings.data.data)
              }
            }
          }
        }
      }
      setUser(updatedUser)
      setHasLicense(permitted(PERMISSION_LEVEL.LICENSED))
      setLoading(false)
    }
    setLoading(true)
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <HomePage data-testid='my-journey-page'>
      {fullAssessmentUser ? (
        <FullAssessmentHome user={user} />
      ) : user ? (
        <>
          {hasLicense ? (
            <Stack spacing={16} padding={6} paddingBottom={16}>
              <WelcomeBanner
                showJouneyButton={isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT)}
              />
              {isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT) && (
                <>
                  <RowStack>
                    <Typography variant='h1'>{t('headers.journey')}</Typography>
                    <ToggleTabs
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}>
                      <Tab value={0} label={t('headers.currentPath')} />
                      <Tab value={1} label={t('home.myPaths')} />
                    </ToggleTabs>
                  </RowStack>
                  {activeTab === 0 ? (
                    <CurrentPathArea
                      goal={currentGoal}
                      futureSession={futureSession}
                      cohortRanking={cohortRanking}
                      showMyPath={() => setActiveTab(1)}
                    />
                  ) : (
                    <MyPathArea goals={goals} />
                  )}
                </>
              )}
              {activeTab === 0 && (
                <>
                  <ActivityLists />
                  {!isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT) && (
                    <NoGoalContent />
                  )}
                  <PathList goals={goals} />
                </>
              )}
            </Stack>
          ) : (
            <RaterHomeLayout />
          )}
          <HomePopups startingGoalName={startingGoalName} />
        </>
      ) : null}
      <OverlayLoading open={loading} />
    </HomePage>
  )
}

export default MyJourney
